import React from 'react';
import { style } from '@project/store/Legend';

const BASE_PREVIEW_ITEM_STYLE: React.CSSProperties = {
  width: '24px',
  height: '24px',
  display: 'inline-block',
  boxSizing: 'border-box',
};

interface Polygon {
  fill: {
    color?: style.Color;
    colorRange?: style.ColorRange;
    pattern?: {
      img?: string;
    };
  };
  border: {
    color: style.Color;
    colorRange?: style.ColorRange;
  };
}

interface Props {
  text?: {
    fill: style.Color;
    colorRange?: style.ColorRange;
  };
  point?: {
    icon?: {
      src?: string;
    };
    circle?: {
      style: Polygon;
    };
  };
  line?: {
    color: style.Color;
    colorRange?: style.ColorRange;
  };
  polygon?: Polygon;
  isCondensed?: boolean;
}

export function getColor(color: style.Color) {
  if (typeof color === 'string') {
    return color;
  } else if (typeof color === 'object') {
    let { r, g, b, a } = color;

    return `rgba( ${[r, g, b, a].join(',')} )`;
  }

  return;
}

const ColorRangeView: React.FC<{
  isCondensed?: boolean;
  colorRange: style.ColorRange;
  style: React.CSSProperties;
}> = ({ colorRange, style, isCondensed = false }) => {
  let gradient = `linear-gradient(to right, ${getColor(
    colorRange.start.color,
  )} 0%,${getColor(colorRange.end.color)} 100%)`;

  if (isCondensed) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: '24px',
          fontSize: '16px',
        }}
      >
        <span style={style} />
        &nbsp;/&nbsp;
        <span
          style={{
            width: '24px',
            background: gradient,
            display: 'inline-block',
            height: '24px',
          }}
        />
      </span>
    );
  } else {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: '24px',
          fontSize: '16px',
        }}
      >
        <span style={style} />
        &nbsp;/&nbsp;
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '24px',
          }}
        >
          {colorRange.fieldName}: &nbsp; [{colorRange.start.value}
          &nbsp;
          <span
            style={{
              width: '64px',
              background: gradient,
              display: 'inline-block',
              height: '1em',
            }}
          />
          &nbsp;
          {colorRange.end.value}]
        </span>
      </span>
    );
  }
};

const Text: React.FC<{
  value: Exclude<Props['text'], undefined>;
}> = ({ value }) => {
  let style: React.CSSProperties = {
    color: getColor(value.fill),
    fontSize: '24px',
    lineHeight: '24px',
  };

  return <span style={style}>A</span>;
};

const Point: React.FC<{
  value: Exclude<Props['point'], undefined>;
  isCondensed?: boolean;
}> = ({ value: { circle, icon }, isCondensed = false }) => {
  let style: React.CSSProperties = {
    ...BASE_PREVIEW_ITEM_STYLE,
    borderRadius: '100%',
  };

  if (circle) {
    Object.assign(style, {
      borderColor: getColor(circle.style.border.color),
      borderWidth: '2px',
      borderStyle: 'solid',
    });

    if (circle.style.fill.color) {
      style.backgroundColor = getColor(circle.style.fill.color);
    }

    if (circle.style.fill.pattern && circle.style.fill.pattern.img) {
      Object.assign(style, {
        backgroundImage: `url(${circle.style.fill.pattern.img})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      });
    }
  }

  if (circle && circle.style.fill.colorRange) {
    return (
      <ColorRangeView
        style={style}
        colorRange={circle.style.fill.colorRange}
        isCondensed={isCondensed}
      />
    );
  }

  if (icon && icon.src) {
    Object.assign(style, {
      backgroundImage: `url(${icon.src})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
    });
  }

  return <span style={style} />;
};

const Line: React.FC<{ value: Exclude<Props['line'], undefined> }> = ({
  value,
}) => {
  return (
    <svg style={BASE_PREVIEW_ITEM_STYLE} viewBox="0 0 31.974 31.974">
      <path
        style={{ fill: getColor(value.color) }}
        d={`M2.224,31.831c0.206,0.097,0.423,0.143,0.637,0.143c0.563,0,1.103-0.318,1.358-0.862l8.728-18.587
  l6.581,7.894c0.328,0.393,0.836,0.595,1.344,0.526c0.509-0.065,0.948-0.386,1.166-0.85L30.47,2.138
  c0.352-0.75,0.03-1.644-0.72-1.995C29-0.21,28.105,0.112,27.755,0.863l-7.431,15.825l-6.581-7.894
  c-0.328-0.393-0.839-0.597-1.344-0.526c-0.509,0.065-0.948,0.386-1.166,0.85L1.504,29.837C1.152,30.586,1.474,31.48,2.224,31.831z`}
      />
    </svg>
  );
};

const Polygon: React.FC<{
  value: Exclude<Props['polygon'], undefined>;
  isCondensed?: boolean;
}> = ({ value: { fill, border }, isCondensed = false }) => {
  let style: React.CSSProperties = {
    ...BASE_PREVIEW_ITEM_STYLE,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: getColor(border.color),
  };

  if (fill.color) {
    style.backgroundColor = getColor(fill.color);
  }

  if (fill.colorRange) {
    return (
      <ColorRangeView
        style={style}
        colorRange={fill.colorRange}
        isCondensed={isCondensed}
      />
    );
  }

  if (fill.pattern && fill.pattern.img) {
    Object.assign(style, {
      backgroundImage: `url(${fill.pattern.img})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
    });
  }

  return <span style={style} />;
};

export const ItemPreview: React.FC<Props> = ({
  line,
  point,
  polygon,
  text,
  isCondensed = false,
}) => (
  <span style={{ display: 'inline-flex' }}>
    {text && <Text value={text} />}
    {point && <Point value={point} isCondensed={isCondensed} />}
    {line && <Line value={line} />}
    {polygon && <Polygon value={polygon} isCondensed={isCondensed} />}
  </span>
);
